import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Typography, Button, Box, CircularProgress } from '@material-ui/core';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useParams, useLocation } from 'react-router-dom';
import { RENTAL_ITEMS_BOOKING } from 'graphql/queries/rental-items-booking';
import { CANCEL_RENTAL_ITEMS_BOOKING } from 'graphql/mutations/booking';
import useStyles from './styles';

const RentalInvoice = ({ rentalItemsData, loading, error, onCancelBooking, cancellationAction, isCanceled, isCancelling }) => {
  const classes = useStyles();

  
const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);
    
    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  const getDayNamesBetweenDates = (startDate, endDate) => {
    const days = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);
    
    while (currentDate <= lastDate) {
      days.push(currentDate.toLocaleDateString('en-US', { weekday: 'long' }));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const getWeekdayWeekendDayNames = (startDate, endDate) => {
    const allDays = getDayNamesBetweenDates(startDate, endDate);
    const weekdays = allDays.filter(day => 
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(day)
    );
    const weekends = allDays.filter(day => 
      ['Saturday', 'Sunday'].includes(day)
    );
    
    return { weekdays, weekends };
  };

  if (loading) {
    return <div className={classes.loading}>Loading invoice...</div>;
  }

  if (error) {
    return <div className={classes.error}>Error loading invoice: {error.message}</div>;
  }

  if (!rentalItemsData?.rentalItemsBookings?.length) {
    return <div className={classes.empty}>No booking data available</div>;
  }

  const booking = rentalItemsData.rentalItemsBookings[0];
  const payment = booking.payments[0];
  const units = booking.units[0];
  const currency = payment?.currency?.currency || 'NOK';

  const getBookingPricesData = () => {
    try {
      const bookingPrices = JSON.parse(units.bookingPrices);
      if (!Array.isArray(bookingPrices) || bookingPrices.length === 0) {
        return { pricingType: 'unknown', totalPrice: units.totalPrice || 0, currency };
      }

      const firstPrice = bookingPrices[0];
      const pricesType = firstPrice.prices_type;

      
      let weekdayDayNames = [];
      let weekendDayNames = [];
      
      if (pricesType === 'pricesWeeklyForm') {
        const dayNames = getWeekdayWeekendDayNames(booking.fromDate, booking.toDate);
        weekdayDayNames = dayNames.weekdays;
        weekendDayNames = dayNames.weekends;
      }

      switch (pricesType) {
        case 'pricesFixedForm':
          return {
            pricingType: 'fixed',
            priceValue: firstPrice.total / (units.days || 1),
            totalPrice: units.totalPrice || firstPrice.total,
            days: firstPrice.days,
            currency: firstPrice.currency || currency
          };

        case 'pricesWeeklyForm':
          const weekdayData = bookingPrices.find(p => p.type === 'weekday');
          const weekendData = bookingPrices.find(p => p.type === 'weekend');
          
          return {
            pricingType: 'weekly',
            weekdayDays: weekdayData ? weekdayData.days : 0,
            weekendDays: weekendData ? weekendData.days : 0,
            weekdayRate: weekdayData ? weekdayData.per_day_price : 0,
            weekendRate: weekendData ? weekendData.per_day_price : 0,
            weekdayTotal: weekdayData ? weekdayData.total : 0,
            weekendTotal: weekendData ? weekendData.total : 0,
            totalPrice: units.totalPrice || bookingPrices.reduce((sum, p) => sum + p.total, 0),
            currency: firstPrice.currency || currency,
            weekdayDayNames,
            weekendDayNames
          };

        case 'pricesDailyForm':
          
          const allDates = getDatesBetween(booking.fromDate, booking.toDate);
          
          return {
            pricingType: 'daily',
            dailyPrices: bookingPrices.map((price, index) => ({
              day: price.day,
              date: allDates[index] ? formatDate(allDates[index]) : price.day,
              price: price.price,
              currency: price.currency || currency
            })),
            totalPrice: units.totalPrice || bookingPrices.reduce((sum, p) => sum + p.price, 0),
            currency: firstPrice.currency || currency
          };

        case 'pricesHourlyForm':
          return {
            pricingType: 'hourly',
            hourlyPrices: bookingPrices.map(price => ({
              time: price.time,
              timestamp: price.timestamp,
              price: price.price,
              currency: price.currency || currency
            })),
            totalPrice: units.totalPrice || bookingPrices.reduce((sum, p) => sum + p.price, 0),
            currency: firstPrice.currency || currency
          };

        default:
          return {
            pricingType: 'unknown',
            totalPrice: units.totalPrice || 0,
            currency: firstPrice.currency || currency
          };
      }
    } catch (error) {
   
      return {
        pricingType: 'unknown',
        totalPrice: units.totalPrice || 0,
        currency
      };
    }
  };

  const pricingInfo = getBookingPricesData();



 const formatTime = (dateTimeString) => {
  const date = new Date(dateTimeString);
  
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false,
  });
};

  
  const fromDate = new Date(booking.fromDate);
  const toDate = new Date(booking.toDate);
  const totalDays = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;

  const addOnsTotal = booking.bookedAddOns?.reduce((sum, addOn) => {
    return sum + (addOn.totalPrice || 0);
  }, 0) || 0;

  const finalTotal = pricingInfo.totalPrice + addOnsTotal;

  return (
    <div className={classes.invoiceContainer}>
      <div className={classes.invoiceHeader}>
        <div className={classes.receiptIcon}>🧾</div>
        <div className={classes.headerText}>
          <h1>RENTAL INVOICE</h1>
          <p>Invoice #{booking.referenceNumber}</p>
        </div>
        
        {cancellationAction && (
          <Box className={classes.cancellationSection}>
            <Button
              variant="contained"
              color="secondary"
              onClick={onCancelBooking}
              disabled={isCanceled || isCancelling}
              className={classes.cancelButton}
              startIcon={isCancelling ? <CircularProgress size={16} /> : null}
            >
              {isCancelling ? 'Cancelling...' : isCanceled ? 'Booking Canceled' : 'Cancel Booking'}
            </Button>
            {isCanceled && (
              <Typography variant="body2" color="error" className={classes.cancellationMessage}>
                This booking has been canceled
              </Typography>
            )}
          </Box>
        )}
      </div>

      <div className={classes.infoGrid}>
        <div className={classes.infoCard}>
          <h3>👤 Customer Details</h3>
          <p><strong>Name:</strong> {booking.guestFirstName} {booking.guestLastName}</p>
          <p><strong>Email:</strong> {booking.guestEmail}</p>
          <p><strong>Phone:</strong> {booking.guestPhoneNumber}</p>
        </div>
        
        <div className={classes.infoCard}>
          <h3>📅 Rental Period</h3>
          <p><strong>Date:</strong> {formatDate(booking.fromDate)}</p>
          {pricingInfo.pricingType === 'hourly' ? (
            <>
              <p><strong>Start Time:</strong> {booking.startTime ? formatTime(booking.startTime) : 'N/A'}</p>
              <p><strong>End Time:</strong> {booking.endTime ? formatTime(booking.endTime) : 'N/A'}</p>
              <p><strong>Duration:</strong> {pricingInfo.hourlyPrices?.length || 0} hour{(pricingInfo.hourlyPrices?.length || 0) > 1 ? 's' : ''}</p>
            </>
          ) : (
            <>
              <p><strong>From:</strong> {formatDate(booking.fromDate)}</p>
              <p><strong>To:</strong> {formatDate(booking.toDate)}</p>
              <p><strong>Duration:</strong> {totalDays} day{totalDays > 1 ? 's' : ''}</p>
            </>
          )}
          <span className={`${classes.status} ${booking.isCanceled ? classes.canceledStatus : classes.activeStatus}`}>
            {booking.isCanceled ? 'CANCELED' : 'ACTIVE'}
          </span>
        </div>
      </div>

      <table className={classes.invoiceTable}>
        <thead>
          <tr>
            {pricingInfo.pricingType === 'hourly' ? (
              <>
                <th>Time Slot</th>
                <th>Rate</th>
                <th>Units</th>
                <th className={classes.textRight}>Amount</th>
              </>
            ) : pricingInfo.pricingType === 'fixed' ? (
              <>
                <th>Description</th>
                <th>Units</th>
                <th>Days</th>
                <th>Rate</th>
                <th className={classes.textRight}>Amount</th>
              </>
            ) : pricingInfo.pricingType === 'daily' ? (
              <>
                <th>Day</th>
                <th>Date</th>
                <th>Rate</th>
                <th>Units</th>
                <th className={classes.textRight}>Amount</th>
              </>
            ) : (
              <>
                <th>Description</th>
                <th>Days</th>
                <th>Rate</th>
                <th>Units</th>
                <th className={classes.textRight}>Amount</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {pricingInfo.pricingType === 'hourly' && pricingInfo.hourlyPrices && (
            pricingInfo.hourlyPrices.map((hour, index) => (
              <tr key={index}>
                <td>{hour.time}</td>
                <td className={classes.textCenter}>{hour.price} {hour.currency}</td>
                <td className={classes.textCenter}>{units.unitsNumber}</td>
                <td className={classes.textRight}>{(hour.price * units.unitsNumber).toLocaleString()} {hour.currency}</td>
              </tr>
            ))
          )}

          {pricingInfo.pricingType === 'fixed' && (
            <tr>
              <td>Fixed Price Rental</td>
              <td className={classes.textCenter}>{units.unitsNumber}</td>
              <td className={classes.textCenter}>{pricingInfo.days}</td>
              <td className={classes.textCenter}>
                {pricingInfo.priceValue} {pricingInfo.currency}
              </td>
              <td className={classes.textRight}>
                {pricingInfo.totalPrice.toLocaleString()} {pricingInfo.currency}
              </td>
            </tr>
          )}

          {pricingInfo.pricingType === 'weekly' && (
            <>
              {pricingInfo.weekdayDays > 0 && (
                <tr>
                  <td>
                    Weekday Rental
                    {pricingInfo.weekdayDayNames && pricingInfo.weekdayDayNames.length > 0 && (
                      <div className={classes.dayList}>
                        ({pricingInfo.weekdayDayNames.join(', ')})
                      </div>
                    )}
                  </td>
                  <td className={classes.textCenter}>{pricingInfo.weekdayDays}</td>
                  <td className={classes.textCenter}>{pricingInfo.weekdayRate} {pricingInfo.currency}</td>
                  <td className={classes.textCenter}>{units.unitsNumber}</td>
                  <td className={classes.textRight}>
                    {(pricingInfo.weekdayTotal * units.unitsNumber).toLocaleString()} {pricingInfo.currency}
                  </td>
                </tr>
              )}
              {pricingInfo.weekendDays > 0 && (
                <tr>
                  <td>
                    Weekend Rental
                    {pricingInfo.weekendDayNames && pricingInfo.weekendDayNames.length > 0 && (
                      <div className={classes.dayList}>
                        ({pricingInfo.weekendDayNames.join(', ')})
                      </div>
                    )}
                  </td>
                  <td className={classes.textCenter}>{pricingInfo.weekendDays}</td>
                  <td className={classes.textCenter}>{pricingInfo.weekendRate} {pricingInfo.currency}</td>
                  <td className={classes.textCenter}>{units.unitsNumber}</td>
                 <td className={classes.textRight}>
                  {(pricingInfo.weekendTotal * units.unitsNumber).toLocaleString()} {pricingInfo.currency}
                </td>
                </tr>
              )}
            </>
          )}

          {pricingInfo.pricingType === 'daily' && pricingInfo.dailyPrices && (
            pricingInfo.dailyPrices.map((day, index) => (
              <tr key={index}>
                <td>{day.day}</td>
                <td>{day.date}</td>
                <td className={classes.textCenter}>{day.price} {day.currency}</td>
                <td className={classes.textCenter}>{units.unitsNumber}</td>
                <td className={classes.textRight}>{(day.price * units.unitsNumber).toLocaleString()} {day.currency}</td>
              </tr>
            ))
          )}

          {booking.bookedAddOns?.map((addOn, index) => (
            <tr key={`addon-${index}`}>
              <td>{addOn.addOn[0]?.title || 'Add-on'}</td>
              <td className={classes.textCenter}>-</td>
              <td className={classes.textCenter}>-</td>
              <td className={classes.textCenter}>{addOn.addOnCount}</td>
              <td className={classes.textRight}>{addOn.totalPrice.toLocaleString()} {addOn.currency.currency}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={classes.paymentSummary}>
        <h3>Payment Summary</h3>
        <div className={classes.summaryRow}>
          <span>Rental Subtotal:</span>
          <span>{pricingInfo.totalPrice.toLocaleString()} {pricingInfo.currency}</span>
        </div>
        {addOnsTotal > 0 && (
          <div className={classes.summaryRow}>
            <span>Add-ons:</span>
            <span>{addOnsTotal.toLocaleString()} {pricingInfo.currency}</span>
          </div>
        )}
        <div className={`${classes.summaryRow} ${classes.totalRow}`}>
          <span>Total Amount:</span>
          <span>{finalTotal.toLocaleString()} {pricingInfo.currency}</span>
        </div>
      </div>

      <div className={classes.invoiceFooter}>
        <p>Invoice generated on {new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}</p>
      </div>
    </div>
  );
};


const InvoiceWrapper = () => {
  const { id, token } = useParams();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [isCanceled, setIsCanceled] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationMessage, setCancellationMessage] = useState('');

  const [cancelRentalItemsBooking] = useMutation(CANCEL_RENTAL_ITEMS_BOOKING);
  
  const cancelRental = location.pathname.indexOf('cancel-rental_items-booking') > 0;
  const cancellationAction = cancelRental && !isCanceled;

  const {
    loading: rentalItemsLoading,
    error: rentalItemsError,
    data: rentalItemsData,
    refetch
  } = useQuery(RENTAL_ITEMS_BOOKING, {
    skip: !id || !token,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      rentalItemsBookingId: parseInt(id),
      token: token
    }
  });
  
  const handleCancellationBooking = async () => {
    if (!id || !token) {
      enqueueSnackbar('Missing booking ID or token', { variant: 'error' });
      return;
    }

    setIsCancelling(true);
    
    try {
      const { data } = await cancelRentalItemsBooking({
        variables: {
          ids: [parseInt(id)],
          token: token
        }
      });

      if (data?.cancelRentalItemsBooking?.ok) {
        setIsCanceled(true);
        setCancellationMessage('Your booking was successfully cancelled');
        enqueueSnackbar('Booking was canceled successfully', { variant: 'success' });
        
        await refetch();
      } else {
        enqueueSnackbar('Failed to cancel booking', { variant: 'error' });
      }
    } catch (error) {
      
      enqueueSnackbar('Error canceling booking: ' + error.message, { variant: 'error' });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <>
      <RentalInvoice 
        rentalItemsData={rentalItemsData}
        loading={rentalItemsLoading}
        error={rentalItemsError}
        onCancelBooking={handleCancellationBooking}
        cancellationAction={cancellationAction}
        isCanceled={isCanceled}
        isCancelling={isCancelling}
      />
      
      {cancellationMessage && (
        <Box style={{ padding: '20px', textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {cancellationMessage}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default InvoiceWrapper;